import { Bewerber, BewerberStatus } from './bewerber';

export enum VorstellungsgespraechStatus {
  ENTWURF = 'ENTWURF',
  ABGESCHLOSSEN = 'ABGESCHLOSSEN',
}

/**
 * Statuses in which the interview tool is available. Mirrors VG_ALLOWED_STATUSES in
 * backend/src/modules/vorstellungsgespraech/vorstellungsgespraech.service.ts, which is
 * what actually enforces it.
 */
export const VG_ALLOWED_STATUSES: BewerberStatus[] = [
  BewerberStatus.VORSTELLUNG,
  BewerberStatus.IM_POOL,
  BewerberStatus.KENNENLERNGESPRAECH,
  BewerberStatus.ONBOARDING,
  BewerberStatus.GEWONNEN,
];

/**
 * Bewerber columns the questionnaire may write when an interview is completed.
 *
 * The backend keeps its own copy of this mapping in
 * backend/src/modules/vorstellungsgespraech/vorstellungsgespraech.definition.ts and only
 * honours the keys listed there — the `mapsTo` entries below are a UI hint, not the
 * authority. Keep the two in sync.
 */
export type VgMappableField = keyof Pick<
  Bewerber,
  | 'angebotenerStundensatz'
  | 'geforderterStundensatz'
  | 'availableFrom'
  | 'kuendigungsfrist'
  | 'minWochenstunden'
  | 'maxWochenstunden'
  | 'hasFuehrerschein'
  | 'hasAuto'
  | 'hasOepnv'
  | 'maxPendelKm'
  | 'maxPendelMinuten'
  | 'geeignetFuer'
>;

export type VgFieldType =
  | 'freitext'
  | 'text'
  | 'single'
  | 'multi'
  | 'date'
  | 'time'
  | 'number'
  | 'boolean'
  | 'info';

export type VgAnswerValue = string | string[] | number | boolean | null;

/** Flat key -> answer map. Stored verbatim as Vorstellungsgespraech.answers. */
export type VgAnswers = Record<string, VgAnswerValue>;

export interface VgOption {
  value: string;
  label: string;
}

export interface VgField {
  key: string;
  type: VgFieldType;
  label?: string;
  placeholder?: string;
  options?: VgOption[];
  defaultValue?: VgAnswerValue;
  /** Unit shown after the input, e.g. 'Std.', 'km', '€'. */
  suffix?: string;
  min?: number;
  max?: number;
  /** Height of a freitext field. Defaults to 3. */
  rows?: number;
  /** Paragraphs rendered by an 'info' field (static text the interviewer reads out). */
  body?: string[];
  mapsTo?: VgMappableField;
}

export type VgSection =
  | {
      kind: 'instruction';
      key: string;
      title: string;
      /** Paragraphs the interviewer reads out. */
      body?: string[];
    }
  | {
      kind: 'question';
      key: string;
      title: string;
      hint?: string;
      fields: VgField[];
    }
  | {
      /**
       * A section that shows something instead of asking: it stores no answer and
       * never reaches the protocol. The interviewer reads it during the call.
       */
      kind: 'widget';
      key: string;
      title: string;
      hint?: string;
      widget: 'BEDARFE_IN_REICHWEITE';
    };

/** The rate communicated to candidates, used when the Bewerber has none on file yet. */
export const KOMMUNIZIERTER_STUNDENSATZ = 15;

/** Matches the @Max(50) validator on the backend DTO. */
const MAX_WOCHENSTUNDEN = 50;

/**
 * The interview script, walked top to bottom during the call. Single source of truth for
 * what the questionnaire page renders.
 */
export const VORSTELLUNGSGESPRAECH_SECTIONS: VgSection[] = [
  {
    kind: 'instruction',
    key: 'begruessung',
    title: 'Begrüßung',
  },
  {
    kind: 'instruction',
    key: 'meineVorstellung',
    title: 'Meine Vorstellung',
    body: [
      'Mein Name ist [Name] und ich bin bei Juniva als Recruiterin tätig. Ich führe heute das Gespräch mit Ihnen und möchte Sie, Ihre Erfahrungen und Ihre Motivation etwas besser kennenlernen.',
      'Im Anschluss halte ich die wichtigsten Punkte fest und gebe meine Einschätzung intern weiter. Antworten Sie einfach offen und ehrlich.',
    ],
  },
  {
    kind: 'instruction',
    key: 'junivaVorstellen',
    title: 'Stelle Juniva vor',
    body: [
      'Juniva unterstützt Kinder und Jugendliche, die im Schulalltag Begleitung brauchen. Unsere Mitarbeiterinnen und Mitarbeiter helfen ihnen dabei, möglichst selbstständig am Unterricht und am sozialen Miteinander teilzunehmen.',
      'Je nach Kind kann die Unterstützung ganz unterschiedlich aussehen – zum Beispiel bei der Orientierung, im Umgang mit anderen, bei emotionalen Herausforderungen oder auch bei körperlichen und pflegerischen Aufgaben.',
    ],
  },
  {
    kind: 'instruction',
    key: 'bewerberVorstellen',
    title: 'Bewerber soll sich vorstellen',
  },
  {
    kind: 'question',
    key: 'arbeitsalltag',
    title: 'Wie stellen sie sich den Arbeitsalltag eines Schulbegleiters vor?',
    fields: [
      {
        key: 'arbeitsalltagBewertung',
        type: 'single',
        options: [
          { value: 'passt', label: 'Vorstellung passt' },
          { value: 'passt_teilweise', label: 'Vorstellung passt teilweise' },
          { value: 'passt_nicht', label: 'Vorstellung passt nicht' },
        ],
      },
    ],
  },
  {
    kind: 'question',
    key: 'fallbeispiel',
    title:
      'Ein Kind bekommt im Kunst-Unterricht die Aufgabe ein Pferd zu malen. Das gemalte Pferd sieht aus der Perspektive der Lehrerin etwas seltsam aus. Plötzlich wird das Kind unruhig, laut und wirft sogar einen Stift durch die Klasse. Wie gehen Sie damit um?',
    hint: 'Beispiel vorlesen und ankreuzen, welche Aspekte der Bewerber nennt.',
    fields: [
      {
        key: 'fallbeispielFlags',
        type: 'multi',
        options: [
          { value: 'empathie', label: 'Empathie' },
          { value: 'deeskalation', label: 'Deeskalation' },
          { value: 'ruhe', label: 'Ruhe' },
          { value: 'loesungsorientiert', label: 'Lösungsorientiertes Denken' },
        ],
      },
      { key: 'fallbeispielText', type: 'freitext', placeholder: 'Notizen zur Antwort…' },
    ],
  },
  {
    kind: 'question',
    key: 'belastung',
    title:
      'Kinder können auch schon mal schwierig und „gemein“ sein, was einen belasten kann. Was machen Sie, wenn Sie gestresst oder emotional angegriffen sind?',
    fields: [
      {
        key: 'belastungStrategie',
        type: 'single',
        options: [
          { value: 'ja', label: 'Ja, hat eine Strategie zur Bewältigung' },
          { value: 'nein', label: 'Nein, hat keine Strategie zur Bewältigung' },
        ],
      },
      { key: 'belastungText', type: 'freitext', placeholder: 'Notizen zur Antwort…' },
    ],
  },
  {
    kind: 'question',
    key: 'konditionen',
    title: 'Konditionen (Stundensatz, Urlaub & Stundenkonto)',
    fields: [
      {
        key: 'stundensatzLegende',
        type: 'info',
        label: 'Stundensätze',
        body: [
          'Quereinsteiger: 15 €',
          'Mit Weiterbildung: 16 €',
          'Ausbildung (pädagogisch): 17 €',
          'Studium (pädagogisch): 19 €',
        ],
      },
      {
        key: 'angebotenerStundensatz',
        type: 'number',
        label: 'Angebotener Stundensatz',
        suffix: '€',
        min: 0,
        mapsTo: 'angebotenerStundensatz',
      },
      {
        key: 'minStundensatz',
        type: 'number',
        label: 'Geforderter Stundensatz (falls nicht passend)',
        suffix: '€',
        min: 0,
        mapsTo: 'geforderterStundensatz',
      },
      {
        key: 'urlaubHinweis',
        type: 'info',
        label: 'Erklärung zu Urlaub & Ferienzeit',
        body: [
          'Als Schulbegleitung haben Sie 30 Urlaubstage im Jahr. Da die Schulferien insgesamt länger sind, arbeiten wir zusätzlich mit einem Stundenkonto.',
          'Das bedeutet: Während der Schulzeit wird ein kleiner Teil Ihrer Arbeitsstunden angespart. Bei einer 25-Stunden-Woche werden zum Beispiel 20 Stunden direkt ausgezahlt und 5 Stunden auf dem Stundenkonto gesammelt.',
          'Diese angesparten Stunden werden später für die Ferien genutzt. So bekommen Sie auch in den schulfreien Zeiten weiterhin Ihr Gehalt.',
          'Die genaue Berechnung erklären wir Ihnen natürlich noch einmal in Ruhe und sie wird auch im Arbeitsvertrag festgehalten.',
        ],
      },
      {
        key: 'konditionenVerstanden',
        type: 'boolean',
        label: 'Konditionen klar und verstanden?',
      },
    ],
  },
  {
    kind: 'question',
    key: 'verfuegbarkeit',
    title: 'Ab wann sind Sie verfügbar?',
    fields: [
      { key: 'verfuegbarAb', type: 'date', label: 'Ab', mapsTo: 'availableFrom' },
      {
        key: 'kuendigungsfrist',
        type: 'text',
        label: 'Kündigungsfrist',
        placeholder: 'z.B. 4 Wochen zum Monatsende',
        mapsTo: 'kuendigungsfrist',
      },
    ],
  },
  {
    kind: 'question',
    key: 'arbeitszeit',
    title: 'Wie wollen Sie zeitlich arbeiten?',
    fields: [
      { key: 'zeitfensterVon', type: 'time', label: 'Zeitfenster von' },
      { key: 'zeitfensterBis', type: 'time', label: 'bis' },
      {
        key: 'minWochenstunden',
        type: 'number',
        label: 'Min. Stunden',
        suffix: 'Std.',
        defaultValue: 30,
        min: 0,
        max: MAX_WOCHENSTUNDEN,
        mapsTo: 'minWochenstunden',
      },
      {
        key: 'maxWochenstunden',
        type: 'number',
        label: 'Max. Stunden',
        suffix: 'Std.',
        defaultValue: 40,
        min: 0,
        max: MAX_WOCHENSTUNDEN,
        mapsTo: 'maxWochenstunden',
      },
    ],
  },
  {
    kind: 'question',
    key: 'fuehrerschein',
    title: 'Haben sie einen Führerschein und ein eigenes Auto?',
    fields: [
      { key: 'hasFuehrerschein', type: 'boolean', label: 'Führerschein', mapsTo: 'hasFuehrerschein' },
      { key: 'hasAuto', type: 'boolean', label: 'Eigenes Auto', mapsTo: 'hasAuto' },
      { key: 'hasOepnv', type: 'boolean', label: 'ÖPNV', mapsTo: 'hasOepnv' },
    ],
  },
  {
    kind: 'question',
    key: 'pendeln',
    title: 'Wie weit würden Sie pendeln?',
    fields: [
      { key: 'maxPendelKm', type: 'number', label: 'Entfernung', suffix: 'km', min: 0, mapsTo: 'maxPendelKm' },
      {
        key: 'maxPendelMinuten',
        type: 'number',
        label: 'Fahrtzeit',
        suffix: 'Min.',
        min: 0,
        mapsTo: 'maxPendelMinuten',
      },
    ],
  },
  {
    kind: 'widget',
    key: 'bedarfeInReichweite',
    title: 'Bedarfe in Reichweite',
    hint: 'Ergibt sich aus den Angaben zu Zeiten und Pendelbereitschaft. Knapp danebenliegende Bedarfe bleiben in der Liste und zeigen, was nicht passt.',
    widget: 'BEDARFE_IN_REICHWEITE',
  },
  {
    kind: 'instruction',
    key: 'weitererAblauf',
    title: 'Zum weiteren Ablauf',
    body: [
      'Nach unserem Gespräch leite ich Ihre Unterlagen und meine Einschätzung an die zuständigen Kolleginnen und Kollegen weiter. Diese prüfen dann die nächsten Schritte.',
      'Sobald wir einen konkreten Fall haben, der grundsätzlich infrage kommt, melden wir uns bei Ihnen und stellen Ihnen den Einsatz genauer vor. Sie können sich alles in Ruhe überlegen und entscheiden, ob Sie das Kind kennenlernen möchten.',
      'Wenn Sie Interesse haben, vereinbaren wir ein Kennenlerngespräch vor Ort. Dabei werden Sie von einer Koordinatorin oder einem Koordinator von Juniva begleitet.',
      'Wenn das Kennenlernen für alle Beteiligten passt und alle Seiten zustimmen, kümmern wir uns anschließend um die Formalitäten, wie den Arbeitsvertrag und die Vorbereitung Ihres Einsatzes.',
    ],
  },
  {
    kind: 'question',
    key: 'offeneFragen',
    title: 'Gibt es noch Fragen?',
    fields: [
      {
        key: 'offeneFragenText',
        type: 'freitext',
        placeholder: 'Fragen des Bewerbers und gegebene Antworten…',
      },
    ],
  },
  {
    kind: 'question',
    key: 'fazit',
    title: 'Fazit',
    hint: 'Geeignet für:',
    fields: [
      {
        key: 'geeignetFuer',
        type: 'multi',
        mapsTo: 'geeignetFuer',
        options: [
          { value: 'koerperlich', label: 'Körperlich' },
          { value: 'seelisch', label: 'Seelisch' },
          { value: 'pflegerisch', label: 'Pflegerisch' },
        ],
      },
    ],
  },
  {
    kind: 'question',
    key: 'bemerkungen',
    title: 'Notizen / Bemerkungen',
    hint: 'Alles, was sonst noch aufgefallen ist.',
    fields: [
      {
        key: 'bemerkungenText',
        type: 'freitext',
        placeholder: 'Freie Notizen zum Gespräch…',
        rows: 6,
      },
    ],
  },
];

/**
 * Columns a fresh draft takes from the Bewerber instead of the field default. These are
 * the answers the second part of the website form already delivers (and the screening
 * may have read from the CV), so the interviewer confirms them rather than asking anew.
 * The Fazit and the rates are deliberately not here: they are the interview's own verdict.
 */
const PREFILLED_FROM_BEWERBER = new Set<VgMappableField>([
  'availableFrom',
  'kuendigungsfrist',
  'minWochenstunden',
  'maxWochenstunden',
  'hasFuehrerschein',
  'hasAuto',
  'hasOepnv',
  'maxPendelKm',
  'maxPendelMinuten',
]);

/**
 * Seed answers for a fresh draft. Saved draft answers are spread over these, so a value
 * the interviewer has already touched always wins. The offered rate starts from whatever
 * the Bewerber already has on file, falling back to the standard communicated rate.
 */
export const buildDefaultAnswers = (bewerber: Bewerber): VgAnswers => {
  const defaults: VgAnswers = {};

  for (const section of VORSTELLUNGSGESPRAECH_SECTIONS) {
    if (section.kind !== 'question') continue;
    for (const field of section.fields) {
      if (field.defaultValue !== undefined) defaults[field.key] = field.defaultValue;

      if (!field.mapsTo || !PREFILLED_FROM_BEWERBER.has(field.mapsTo)) continue;
      const stored = bewerber[field.mapsTo];
      if (stored === null || stored === undefined || stored === '') continue;
      // availableFrom arrives as an ISO timestamp; the date input wants YYYY-MM-DD.
      defaults[field.key] =
        field.type === 'date' ? String(stored).slice(0, 10) : (stored as VgAnswerValue);
    }
  }

  // Decimal columns arrive as strings over JSON.
  defaults.angebotenerStundensatz =
    bewerber.angebotenerStundensatz !== null
      ? Number(bewerber.angebotenerStundensatz)
      : KOMMUNIZIERTER_STUNDENSATZ;

  return defaults;
};

export interface Vorstellungsgespraech {
  id: string;
  bewerberId: string;
  status: VorstellungsgespraechStatus;
  answers: VgAnswers | null;
  durchgefuehrtVonId: string;
  durchgefuehrtVon?: { id: string; firstName: string; lastName: string };
  durchgefuehrtAm: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SaveAnswersDto {
  answers: VgAnswers;
}
